/**
 * Card Component
 * Displays playing cards with face-up/face-down states
 */

import React from 'react';
import type { Card as CardType } from '../../../types/game';
import { isRedSuit, getCardDifficulty } from '../../../engine/deck';
import styles from './Card.module.css';

interface CardProps {
  card?: CardType;
  faceDown?: boolean;
  selected?: boolean;
  interactive?: boolean;
  mini?: boolean;
  onClick?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  ariaLabel?: string;
  cardRef?: React.Ref<HTMLButtonElement>;
  tabIndex?: number;
}

export const Card: React.FC<CardProps> = ({
  card,
  faceDown = false,
  selected = false,
  interactive = false,
  mini = false,
  onClick,
  onKeyDown,
  ariaLabel,
  cardRef,
  tabIndex,
}) => {
  // Face-down card
  if (faceDown) {
    return (
      <div
        className={styles.cardBack}
        aria-label={ariaLabel || 'Face down card'}
      />
    );
  }

  if (!card) return null;

  const isRed = isRedSuit(card.suit);
  const difficulty = getCardDifficulty(card.rank);

  // Mini card variant (for question screen preview)
  if (mini) {
    return (
      <div
        className={`${styles.mini} ${isRed ? styles.miniRed : ''}`}
        aria-label={ariaLabel || `${card.rank} of ${card.suit}`}
      >
        <span>{card.rank}</span>
        <span style={{ fontSize: '0.9rem' }}>{card.suit}</span>
        <span style={{ transform: 'rotate(180deg)' }}>{card.rank}</span>
      </div>
    );
  }

  // Standard card classes
  const cardClasses = [
    interactive ? styles.playerCard : styles.cardFront,
    isRed ? styles.red : '',
    selected ? styles.selected : '',
  ].join(' ');

  // Use button element for interactive cards (better keyboard accessibility)
  if (interactive) {
    return (
      <button
        ref={cardRef}
        className={cardClasses}
        onClick={onClick}
        onKeyDown={onKeyDown}
        tabIndex={tabIndex ?? 0}
        aria-label={ariaLabel || `${card.rank} of ${card.suit}, ${difficulty} difficulty`}
        type="button"
      >
        <span className={styles.rankTop}>{card.rank}</span>
        <span className={styles.suitCenter}>{card.suit}</span>
        <span className={styles.rankBot}>{card.rank}</span>
        <span className={`${styles.difficultyBadge} ${styles[difficulty]}`}>
          {difficulty[0].toUpperCase()}
        </span>
      </button>
    );
  }

  return (
    <div
      className={cardClasses}
      aria-label={ariaLabel || `${card.rank} of ${card.suit}, ${difficulty} difficulty`}
    >
      <span className={styles.rankTop}>{card.rank}</span>
      <span className={styles.suitCenter}>{card.suit}</span>
      <span className={styles.rankBot}>{card.rank}</span>
      <span className={`${styles.difficultyBadge} ${styles[difficulty]}`}>
        {difficulty[0].toUpperCase()}
      </span>
    </div>
  );
};

export default Card;
